import React, {useCallback, useEffect, useRef, useState} from "react";
import Styles from './styles.module.scss';
import {useDispatch, useSelector} from "react-redux";
import {toast} from "react-toastify";
import {useTranslation} from "react-i18next";
import {
    changePassword,
    changePasswordFailure,
    changePasswordSuccess,
    setGoogleAuthenticatorConnectModalStatus,
    setSettingsAvatarModalStatus,
    telegramApi,
    profileApi,
    getUserData,
    cabinetLogout
} from "../../store/cabinet/action";
import DsModal from "../_UI/dsModal";
import Form from "../_UI/dsModal/form.module.scss";

import heroArt from '../../theme/assets/redesign/settings/hero.webp';
import avatarPlaceholder from '../../theme/assets/redesign/avatar_placeholder.webp';
import lockIcon from '../../theme/assets/redesign/settings/lock.svg';
import cameraIcon from '../../theme/assets/redesign/settings/camera.svg';
import shieldIcon from '../../theme/assets/redesign/settings/shield.svg';
import phoneIcon from '../../theme/assets/redesign/settings/smartphone.svg';
import bellIcon from '../../theme/assets/redesign/settings/bell.svg';
import trendingIcon from '../../theme/assets/redesign/settings/trending.svg';
import moneyIcon from '../../theme/assets/redesign/settings/money.svg';
import giftIcon from '../../theme/assets/redesign/settings/gift.svg';
import settingsIcon from '../../theme/assets/redesign/settings/settings.svg';
import userIcon from '../../theme/assets/redesign/settings/user.svg';
import {ReactComponent as TelegramIcon} from '../../theme/assets/redesign/social/telegram.svg';
import trashIcon from '../../theme/assets/redesign/settings/trash.svg';
import eyeOffIcon from '../../theme/assets/redesign/settings/eye_off.svg';
import eyeIcon from '../../theme/assets/redesign/settings/eye.svg';

// Значения по умолчанию, пока не пришёл ответ GET /profile/notifications
const NOTIFY_DEFAULTS = {rounds: true, price: true, giveaway: true, system: false};

const firstError = (err, field) => err?.response?.data?.errors?.[field]?.[0] || null;

const PanelHeader = ({icon, title, note}) => (
    <div className={Styles.panelHeader}>
        <div className={Styles.panelTitleRow}>
            <img src={icon} alt=""/>
            <div className={Styles.panelTitle}>{title}</div>
        </div>
        {note && <div className={Styles.panelNote}>{note}</div>}
    </div>
);

const PasswordField = ({label, value, onChange, error, onType, autoComplete}) => {
    const [visible, setVisible] = useState(false);
    return (
        <div className={Styles.pwdRow}>
            <div className={Styles.pwdLabel}>{label}</div>
            <div className={Styles.pwdControl}>
                <div className={`${Styles.input} ${error ? Styles.inputErr : ''}`}>
                    <input
                        type={visible ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={value}
                        autoComplete={autoComplete}
                        onChange={(e) => onChange(e.target.value)}
                        onKeyDown={onType}
                    />
                    <button type="button" className={Styles.eyeBtn} onClick={() => setVisible(v => !v)}>
                        <img src={visible ? eyeIcon : eyeOffIcon} alt=""/>
                    </button>
                </div>
                {error && <div className={Styles.errText}>{error}</div>}
            </div>
        </div>
    );
};


// Страница настроек (макеты 2424:3939 / 2464:5100)
const CabinetSettingsMain = () => {
    const dispatch = useDispatch();
    const {t} = useTranslation();

    const userData = useSelector(store => store.cabinet.userData);
    const changePasswordStatus = useSelector(store => store.cabinet.changePasswordStatus);
    const changePasswordStarted = useSelector(store => store.cabinet.changePasswordStatusStarted);
    const formErr = useSelector(store => store.cabinet.changePasswordStatusErr);

    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [notify, setNotify] = useState(NOTIFY_DEFAULTS);

    const user = userData?.user;
    const mismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;
    const canChangePassword = currentPassword.length >= 8 && newPassword.length >= 8 && newPassword === confirmPassword && !changePasswordStarted;

    const clearErr = (field) => {
        if (formErr?.[field]) {
            const next = {...formErr};
            delete next[field];
            dispatch(changePasswordFailure(next));
        }
    };

    const changePasswordHandler = () => {
        if (canChangePassword) {
            dispatch(changePassword({
                current_password: currentPassword,
                new_password: newPassword,
                new_password_confirmation: confirmPassword
            }));
        }
    };

    useEffect(() => {
        if (changePasswordStatus) {
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            toast(t('Ваш пароль был успешно изменен!'));
            dispatch(changePasswordSuccess(false));
        }
    }, [changePasswordStatus, dispatch, t]);

    // ===== Настройки уведомлений: GET/PUT /profile/notifications =====
    useEffect(() => {
        profileApi.getNotifySettings()
            .then(r => { if (r) setNotify({...NOTIFY_DEFAULTS, ...r}); })
            .catch(() => {});
    }, []);

    const toggleNotify = (key) => {
        const value = !notify[key];
        setNotify(prev => ({...prev, [key]: value})); // сразу переключаем, при ошибке откатываем
        profileApi.setNotifySettings({[key]: value})
            .then(r => { if (r) setNotify({...NOTIFY_DEFAULTS, ...r}); })
            .catch(() => {
                setNotify(prev => ({...prev, [key]: !value}));
                toast(t('Не удалось сохранить настройку'));
            });
    };

    // ===== Никнейм: PUT /profile/change-username =====
    const [nickname, setNickname] = useState('');
    const [nickErr, setNickErr] = useState(null);
    const [nickBusy, setNickBusy] = useState(false);

    useEffect(() => { setNickname(user?.username ?? ''); }, [user?.username]);

    const nickTrimmed = nickname.trim();
    const nickChanged = !!nickTrimmed && nickTrimmed !== (user?.username ?? '');
    const canSaveNick = nickChanged && !nickBusy;

    const saveNickname = () => {
        if (!canSaveNick) return;
        if (!/^[A-Za-z0-9_.-]{3,30}$/.test(nickTrimmed)) {
            setNickErr(t('3–30 символов: латиница, цифры и _ . -'));
            return;
        }
        setNickBusy(true);
        profileApi.changeUsername(nickTrimmed)
            .then(() => {
                toast(t('Никнейм изменён'));
                dispatch(getUserData());
            })
            .catch(err => setNickErr(firstError(err, 'username') || t('Не удалось изменить никнейм')))
            .finally(() => setNickBusy(false));
    };

    // ===== Telegram: привязка чата с ботом для уведомлений =====
    // linked: null — ещё не знаем, true/false — ответ GET /profile/telegram
    const [tg, setTg] = useState({linked: null, username: null});
    const [tgWaiting, setTgWaiting] = useState(false); // ждём, пока пользователь нажмёт Start в боте
    const [tgBusy, setTgBusy] = useState(false);
    const tgPoll = useRef(null);

    const stopTgPoll = useCallback(() => {
        if (tgPoll.current) clearInterval(tgPoll.current);
        tgPoll.current = null;
        setTgWaiting(false);
    }, []);

    const loadTg = useCallback(() => telegramApi.status()
        .then(r => { setTg({linked: !!r?.linked, username: r?.username || null}); return r; })
        .catch(() => setTg(prev => ({...prev, linked: prev.linked ?? !!user?.telegram_linked}))), [user?.telegram_linked]);

    useEffect(() => { loadTg(); }, [loadTg]);
    useEffect(() => stopTgPoll, [stopTgPoll]);

    const linkTelegram = () => {
        if (tgBusy) return;
        // Вкладку открываем сразу по клику, иначе Safari/iOS заблокирует всплывающее окно после запроса
        const win = window.open('', '_blank');
        setTgBusy(true);
        telegramApi.link()
            .then(r => {
                if (!r?.url) throw new Error('no url');
                if (win && !win.closed) win.location.href = r.url;
                else window.open(r.url, '_blank', 'noopener,noreferrer');
                // опрашиваем статус, пока ссылка жива (бэк: 15 минут)
                setTgWaiting(true);
                const deadline = r?.expires_at ? new Date(r.expires_at).getTime() : Date.now() + 15 * 60 * 1000;
                if (tgPoll.current) clearInterval(tgPoll.current);
                tgPoll.current = setInterval(() => {
                    if (Date.now() > deadline) { stopTgPoll(); return; }
                    loadTg().then(st => {
                        if (st?.linked) {
                            stopTgPoll();
                            toast(t('Telegram подключён'));
                        }
                    });
                }, 3000);
            })
            .catch(() => {
                if (win && !win.closed) win.close();
                toast(t('Не удалось получить ссылку на бота, попробуйте ещё раз'));
            })
            .finally(() => setTgBusy(false));
    };

    const unlinkTelegram = (e) => {
        e.stopPropagation();
        if (tgBusy) return;
        setTgBusy(true);
        telegramApi.unlink()
            .then(r => { setTg({linked: !!r?.linked, username: r?.username || null}); toast(t('Telegram отключён')); })
            .catch(() => toast(t('Не удалось отключить Telegram')))
            .finally(() => setTgBusy(false));
    };

    let tgTitle;
    let tgNote;
    if (tg.linked === null) {
        tgTitle = t('Telegram');
        tgNote = t('Загрузка...');
    } else if (tg.linked) {
        tgTitle = t('Telegram подключён');
        tgNote = tg.username ? `@${tg.username}` : t('Уведомления приходят в бот');
    } else if (tgWaiting) {
        tgTitle = t('Ожидаем подтверждения');
        tgNote = t('Нажмите «Start» в боте — мы подключим его автоматически');
    } else {
        tgTitle = t('Подключить Telegram');
        tgNote = t('Получайте уведомления в Telegram-боте');
    }

    // ===== Удаление аккаунта: DELETE /profile {password, otp?} =====
    const [delOpen, setDelOpen] = useState(false);
    const [delPassword, setDelPassword] = useState('');
    const [delOtp, setDelOtp] = useState('');
    const [delErr, setDelErr] = useState({});
    const [delBusy, setDelBusy] = useState(false);
    const need2fa = !!user?.google2fa_enabled;

    const closeDelete = () => {
        setDelOpen(false);
        setDelPassword('');
        setDelOtp('');
        setDelErr({});
    };

    const canDelete = delPassword.length > 0 && (!need2fa || delOtp.length >= 6) && !delBusy;

    const confirmDelete = () => {
        if (!canDelete) return;
        setDelBusy(true);
        setDelErr({});
        profileApi.deleteAccount(need2fa ? {password: delPassword, otp: delOtp} : {password: delPassword})
            .then(() => {
                closeDelete();
                toast(t('Аккаунт удалён'));
                dispatch(cabinetLogout());
            })
            .catch(err => {
                setDelErr({
                    password: firstError(err, 'password'),
                    otp: firstError(err, 'otp'),
                    account: firstError(err, 'account') || (err?.response?.status !== 422 ? t('Не удалось удалить аккаунт') : null),
                });
            })
            .finally(() => setDelBusy(false));
    };

    const notifications = [
        {key: 'rounds', icon: trendingIcon, title: t('Обновления раундов'), note: t('Новые раунды, прогресс и важные изменения')},
        {key: 'price', icon: moneyIcon, title: t('Ценовые уведомления'), note: t('Движение цены токена и ключевые этапы')},
        {key: 'giveaway', icon: giftIcon, title: t('Обновления розыгрышей'), note: t('Новые розыгрыши, победители и награды')},
        {key: 'system', icon: settingsIcon, title: t('Системные уведомления'), note: t('Безопасность, активность аккаунта и другие обновления')},
    ];

    const pwdError = (field) => formErr?.[field]?.[0] || null;

    return (
        <div className={Styles.container}>
            {/* ===== Hero ===== */}
            <section className={Styles.hero}>
                <div className={Styles.heroArt}><img src={heroArt} alt=""/></div>
                <div className={Styles.heroTitle}>{t('Настройки')}</div>
                <p className={Styles.heroText}>{t('Управляйте аккаунтом и настройками')}</p>
            </section>

            <div className={Styles.grid}>
                <div className={Styles.col}>
                    {/* ===== Профиль ===== */}
                    <section className={Styles.panel}>
                        <PanelHeader icon={lockIcon} title={t('Настройки профиля')}/>
                        <div className={Styles.profile}>
                            <div className={Styles.avatarUpload}>
                                <div className={Styles.avatar}>
                                    <img src={user?.avatar || avatarPlaceholder} alt={user?.username || ''}/>
                                </div>
                                <button
                                    type="button"
                                    className={Styles.cameraBtn}
                                    onClick={() => dispatch(setSettingsAvatarModalStatus(true))}
                                    aria-label={t('Сменить аватар')}
                                >
                                    <img src={cameraIcon} alt=""/>
                                </button>
                            </div>
                            <div className={Styles.nickname}>
                                <div className={Styles.fieldLabel}>{t('Ваш никнейм')}</div>
                                <div className={Styles.nicknameField}>
                                    <div className={`${Styles.input} ${Styles.inputTall} ${nickErr ? Styles.inputErr : ''}`}>
                                        <input
                                            type="text"
                                            value={nickname}
                                            maxLength={30}
                                            autoComplete="username"
                                            onChange={(e) => { setNickname(e.target.value); setNickErr(null); }}
                                            onKeyDown={(e) => { if (e.key === 'Enter') saveNickname(); }}
                                        />
                                    </div>
                                    <button type="button" className={`${Styles.btn} ${Styles.btnTall} ${Styles.desktopOnly}`} disabled={!canSaveNick} onClick={saveNickname}>
                                        {t('Сохранить изменения')}
                                    </button>
                                </div>
                                {nickErr && <div className={Styles.errText}>{nickErr}</div>}
                            </div>
                        </div>
                        <button type="button" className={`${Styles.btn} ${Styles.btnTall} ${Styles.mobileOnly}`} disabled={!canSaveNick} onClick={saveNickname}>
                            {t('Сохранить изменения')}
                        </button>
                    </section>

                    {/* ===== Пароль ===== */}
                    <section className={Styles.panel}>
                        <PanelHeader icon={lockIcon} title={t('Сменить пароль')}/>
                        <PasswordField
                            label={t('Текущий пароль')}
                            value={currentPassword}
                            onChange={setCurrentPassword}
                            onType={() => clearErr('current_password')}
                            error={pwdError('current_password')}
                            autoComplete="current-password"
                        />
                        <PasswordField
                            label={t('Новый пароль')}
                            value={newPassword}
                            onChange={setNewPassword}
                            onType={() => clearErr('new_password')}
                            error={pwdError('new_password')}
                            autoComplete="new-password"
                        />
                        <PasswordField
                            label={t('Повторите новый пароль')}
                            value={confirmPassword}
                            onChange={setConfirmPassword}
                            onType={() => clearErr('new_password_confirmation')}
                            error={mismatch ? t('Пароли не совпадают') : pwdError('new_password_confirmation')}
                            autoComplete="new-password"
                        />
                        <button
                            type="button"
                            className={`${Styles.btn} ${Styles.btnFull}`}
                            disabled={!canChangePassword}
                            onClick={changePasswordHandler}
                        >
                            {t('Обновить пароль')}
                        </button>
                    </section>

                    {/* ===== 2FA ===== */}
                    <section className={Styles.panel}>
                        <PanelHeader icon={shieldIcon} title={t('Подключить 2FA')}
                                     note={`${t('Добавьте дополнительный уровень безопасности для вашего аккаунта')}.`}/>
                        <div className={Styles.row2fa}>
                            <img className={Styles.row2faIcon} src={phoneIcon} alt=""/>
                            <div className={Styles.rowText}>
                                <div className={Styles.rowTitle}>{t('Google Authenticator')}</div>
                                <div className={Styles.rowNote}>
                                    {user?.google2fa_enabled
                                        ? t('Двухфакторная защита включена')
                                        : t('Отсканируйте QR-код в приложении, чтобы включить 2FA')}
                                </div>
                            </div>
                            {user?.google2fa_enabled
                                ? <button type="button" className={`${Styles.btn} ${Styles.btn2fa} ${Styles.btnDone}`} disabled>{t('Подключена')}</button>
                                : <button type="button" className={`${Styles.btn} ${Styles.btn2fa}`}
                                          onClick={() => dispatch(setGoogleAuthenticatorConnectModalStatus(true))}>
                                    {t('Подключить')}
                                </button>}
                        </div>
                    </section>
                </div>

                <div className={`${Styles.col} ${Styles.colRight}`}>
                    {/* ===== Уведомления ===== */}
                    <section className={`${Styles.panel} ${Styles.panelGrow}`}>
                        <PanelHeader icon={bellIcon} title={t('Уведомления')} note={t('Выберите, о чём вас уведомлять')}/>
                        {notifications.map(n => (
                            <button type="button" key={n.key} className={Styles.notifyRow} onClick={() => toggleNotify(n.key)}>
                                <img className={Styles.notifyIcon} src={n.icon} alt=""/>
                                <div className={Styles.rowText}>
                                    <div className={Styles.rowTitle}>{n.title}</div>
                                    <div className={Styles.rowNote}>{n.note}</div>
                                </div>
                                <span className={`${Styles.toggle} ${notify[n.key] ? Styles.toggleOn : ''}`} role="switch" aria-checked={!!notify[n.key]}/>
                            </button>
                        ))}
                    </section>

                    {/* ===== Действия с аккаунтом ===== */}
                    <section className={Styles.panel}>
                        <PanelHeader icon={userIcon} title={t('Действия с аккаунтом')} note={t('Управляйте данными аккаунта.')}/>
                        <div
                            role="button"
                            tabIndex={0}
                            className={`${Styles.actionRow} ${Styles.actionInfo} ${tg.linked ? Styles.actionLinked : ''} ${tgBusy ? Styles.actionBusy : ''}`}
                            onClick={tg.linked === false ? linkTelegram : undefined}
                            onKeyDown={(e) => { if (e.key === 'Enter' && tg.linked === false) linkTelegram(); }}
                        >
                            <TelegramIcon className={Styles.actionIcon} aria-hidden="true"/>
                            <div className={Styles.actionText}>
                                <span>{tgTitle}</span>
                                <span>{tgNote}</span>
                            </div>
                            {tg.linked ? (
                                <button type="button" className={Styles.actionSideBtn} onClick={unlinkTelegram} disabled={tgBusy}>
                                    {t('Отключить')}
                                </button>
                            ) : null}
                            {tgWaiting && !tg.linked ? (
                                <button type="button" className={Styles.actionSideBtn} onClick={(e) => { e.stopPropagation(); linkTelegram(); }} disabled={tgBusy}>
                                    {t('Открыть бота')}
                                </button>
                            ) : null}
                        </div>
                        <button type="button" className={`${Styles.actionRow} ${Styles.actionDanger}`} onClick={() => setDelOpen(true)}>
                            <img src={trashIcon} alt=""/>
                            <div className={Styles.actionText}>
                                <span>{t('Удалить аккаунт')}</span>
                                <span>{t('Безвозвратно удалить аккаунт')}</span>
                            </div>
                        </button>
                    </section>
                </div>
            </div>

            {/* ===== Подтверждение удаления аккаунта ===== */}
            <DsModal
                open={delOpen}
                onClose={closeDelete}
                size="sm"
                title={t('Удалить аккаунт?')}
                subtitle={t('Это действие необратимо. Перед удалением выведите USDT с баланса и дождитесь завершения транзакций.')}
            >
                <div className={Form.field}>
                    <div className={Form.label}>{t('Пароль')}</div>
                    <div className={`${Form.inputBox} ${delErr.password ? Form.inputBoxErr : ''}`}>
                        <input
                            type="password"
                            value={delPassword}
                            autoComplete="current-password"
                            onChange={(e) => { setDelPassword(e.target.value); setDelErr(prev => ({...prev, password: null})); }}
                        />
                    </div>
                    <div className={Form.err}>{delErr.password || null}</div>
                </div>
                {need2fa ? (
                    <div className={Form.field}>
                        <div className={Form.label}>{t('Код Google Authenticator')}</div>
                        <div className={`${Form.inputBox} ${delErr.otp ? Form.inputBoxErr : ''}`}>
                            <input
                                type="text"
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                maxLength={6}
                                value={delOtp}
                                onChange={(e) => { setDelOtp(e.target.value.replace(/\D/g, '')); setDelErr(prev => ({...prev, otp: null})); }}
                            />
                        </div>
                        <div className={Form.err}>{delErr.otp || null}</div>
                    </div>
                ) : null}
                {delErr.account ? <div className={Form.err}>{delErr.account}</div> : null}
                <div className={Form.actions}>
                    <button type="button" className={Form.btnNeutral} onClick={closeDelete}>{t('Отмена')}</button>
                    <button type="button" className={`${Styles.btnDanger} ${canDelete ? '' : Form.btnDisabled}`} onClick={confirmDelete}>
                        {delBusy ? t('Удаляем...') : t('Удалить')}
                    </button>
                </div>
            </DsModal>
        </div>
    );
};

export default CabinetSettingsMain;
